import React, { useEffect, useMemo, useState } from 'react';
import { getFirestore, collection, onSnapshot, query } from 'firebase/firestore';
import app from '../../firebase';

const db = getFirestore(app);

const normalizeFilterValue = value => {
  if (typeof value !== 'string' && typeof value !== 'number') return '';
  return String(value).trim();
};

const getScheduleOptions = (schedules, field) => {
  return Array.from(new Set(
    schedules
      .map(row => normalizeFilterValue(row[field]))
      .filter(Boolean)
  )).sort((a, b) => a.localeCompare(b));
};

const formatDetailLabel = value => value
  .replace(/([a-z0-9])([A-Z])/g, '$1 $2')
  .replace(/^./, character => character.toUpperCase());

function DetailValue({ value }) {
  if (value === null || value === undefined || value === '') return <span>—</span>;
  if (typeof value === 'boolean') return <span>{value ? 'Yes' : 'No'}</span>;
  if (typeof value === 'object' && typeof value.toDate === 'function') {
    return <span>{value.toDate().toLocaleString()}</span>;
  }
  if (Array.isArray(value)) {
    if (value.length === 0) return <span>—</span>;
    return (
      <ul style={{ margin: 0, paddingLeft: 20 }}>
        {value.map((item, index) => (
          <li key={index} style={{ marginBottom: 4 }}>
            <DetailValue value={item} />
          </li>
        ))}
      </ul>
    );
  }
  if (typeof value === 'object') {
    const entries = Object.entries(value);
    if (entries.length === 0) return <span>—</span>;
    return (
      <div style={{ display: 'grid', gap: 4 }}>
        {entries.map(([key, item]) => (
          <div key={key}>
            <strong>{formatDetailLabel(key)}:</strong> <DetailValue value={item} />
          </div>
        ))}
      </div>
    );
  }
  return <span>{String(value)}</span>;
}

export default function RoomSchedule() {
  const [schedules, setSchedules] = useState([]);
  const [rooms, setRooms] = useState([]);
  const [detailModal, setDetailModal] = useState(null);
  const [viewportHeight, setViewportHeight] = useState(() => window.innerHeight);
  const [viewMode, setViewMode] = useState('table');
  const [selectedSchoolYear, setSelectedSchoolYear] = useState('');
  const [selectedGridDay, setSelectedGridDay] = useState('');
  const [dayFilter, setDayFilter] = useState('');
  const [roomFilter, setRoomFilter] = useState('');
  const [departmentFilter, setDepartmentFilter] = useState('');
  const [yearLevelFilter, setYearLevelFilter] = useState('');
  const [semesterFilter, setSemesterFilter] = useState('');
  const [sectionFilter, setSectionFilter] = useState('');
  const [currentPage, setCurrentPage] = useState(1);
  const [sortField, setSortField] = useState('department');
  const [sortDirection, setSortDirection] = useState('asc');
  const [statusMessage, setStatusMessage] = useState('');
  const rowsPerPage = 20;

  useEffect(() => {
    const updateViewportHeight = () => setViewportHeight(window.innerHeight);
    window.addEventListener('resize', updateViewportHeight);
    return () => window.removeEventListener('resize', updateViewportHeight);
  }, []);

  useEffect(() => {
    if (!detailModal) return undefined;
    const closeOnEscape = event => {
      if (event.key === 'Escape') setDetailModal(null);
    };
    window.addEventListener('keydown', closeOnEscape);
    return () => window.removeEventListener('keydown', closeOnEscape);
  }, [detailModal]);

  const schoolYears = useMemo(() => {
    return getScheduleOptions(schedules, 'schoolYear');
  }, [schedules]);
  const departmentOptions = useMemo(() => getScheduleOptions(schedules, 'department'), [schedules]);
  const yearLevelOptions = useMemo(() => getScheduleOptions(schedules, 'yearLevel'), [schedules]);
  const semesterOptions = useMemo(() => getScheduleOptions(schedules, 'semester'), [schedules]);
  const sectionOptions = useMemo(() => getScheduleOptions(schedules, 'section'), [schedules]);

  useEffect(() => {
    const q = query(collection(db, 'schedules'));
    const unsubscribe = onSnapshot(q, (snap) => {
      const scheduleList = snap.docs.map(doc => ({ id: doc.id, ...doc.data() }));
      setSchedules(scheduleList);
    }, (err) => {
      console.error('Failed to load schedules', err);
      setStatusMessage('Failed to load room schedules.');
    });

    return unsubscribe;
  }, []);

  useEffect(() => {
    const unsubscribe = onSnapshot(collection(db, 'rooms'), (snap) => {
      const roomList = snap.docs
        .map(doc => ({ id: doc.id, ...doc.data() }))
        .sort((a, b) => {
          const aCreated = a.createdAt || '';
          const bCreated = b.createdAt || '';
          return aCreated.toString().localeCompare(bCreated.toString());
        });
      setRooms(roomList);
    }, (err) => {
      console.error('Failed to load rooms', err);
    });

    return unsubscribe;
  }, []);

  const normalizeDayLabel = (value) => {
    if (!value || typeof value !== 'string') return null;
    const text = value.trim().toLowerCase();
    if (/mon(day)?/.test(text)) return 'Mon';
    if (/tue(sday)?/.test(text)) return 'Tue';
    if (/wed(nesday)?/.test(text)) return 'Wed';
    if (/thu(rsday)?/.test(text)) return 'Thu';
    if (/fri(day)?/.test(text)) return 'Fri';
    if (/sat(urday)?/.test(text)) return 'Sat';
    if (/sun(day)?/.test(text)) return 'Sun';
    if (/\bth\b/.test(text)) return 'Thu';
    if (/\bm\b/.test(text)) return 'Mon';
    if (/\bt\b/.test(text)) return 'Tue';
    if (/\bw\b/.test(text)) return 'Wed';
    if (/\bf\b/.test(text)) return 'Fri';
    if (/\bs\b/.test(text)) return 'Sat';
    return null;
  };

  const parseDayLabels = (value) => {
    if (!value || typeof value !== 'string') return [];
    const parts = value.split(/\r?\n|[\/;,]+/).map(part => part.trim()).filter(Boolean);
    return Array.from(new Set(parts.map(normalizeDayLabel).filter(Boolean)));
  };

  const parseRoomLabels = (value) => {
    if (!value || typeof value !== 'string') return [];
    const parts = value.split(/\r?\n|[\/;,]+/).map(part => part.trim()).filter(Boolean);
    return Array.from(new Set(parts.map(part => part.replace(/\s+/g, ' ').trim()).filter(Boolean)));
  };

  const splitRowSegments = (value) => {
    if (!value || typeof value !== 'string') return [''];
    const lines = value.split(/\r?\n/).map(part => part.trim()).filter(Boolean);
    if (lines.length > 1) return lines;
    return value.split(/\s*\/\s*/).map(part => part.trim()).filter(Boolean);
  };

  const expandSegments = (segments, count) => {
    if (segments.length === 0) return Array(count).fill('');
    if (segments.length === 1 && count > 1) return Array(count).fill(segments[0]);
    return segments.concat(Array(Math.max(0, count - segments.length)).fill(''));
  };

  const getScheduleSegments = (row) => {
    const daySegments = splitRowSegments(row.day);
    const timeSegments = splitRowSegments(row.time);
    const typeSegments = splitRowSegments(row.type);
    const roomSegments = splitRowSegments(row.room);
    const segmentCount = Math.max(daySegments.length, timeSegments.length, typeSegments.length, roomSegments.length);
    const days = expandSegments(daySegments, segmentCount);
    const times = expandSegments(timeSegments, segmentCount);
    const types = expandSegments(typeSegments, segmentCount);
    const rooms = expandSegments(roomSegments, segmentCount);
    return Array.from({ length: segmentCount }, (_, index) => ({
      day: days[index] || '',
      time: times[index] || '',
      type: types[index] || '',
      room: rooms[index] || ''
    }));
  };

  const normalizeRoomKey = (value) => {
    if (!value || typeof value !== 'string') return '';
    return value.replace(/\s+/g, ' ').trim().toLowerCase();
  };

  const findBestRoomLabel = (roomLabel, roomKeys) => {
    const normalized = normalizeRoomKey(roomLabel);
    if (!normalized) return null;
    if (roomKeys.has(normalized)) return roomKeys.get(normalized);

    for (const [key, original] of roomKeys.entries()) {
      if (key.includes(normalized) || normalized.includes(key)) return original;
    }
    return roomLabel;
  };

  const dayOptions = useMemo(() => {
    const days = new Set();
    schedules.forEach(row => {
      parseDayLabels(row.day).forEach(day => days.add(day));
    });
    const order = ['Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat', 'Sun'];
    return Array.from(days).sort((a, b) => order.indexOf(a) - order.indexOf(b));
  }, [schedules]);
  const gridDay = dayFilter || selectedGridDay;

  useEffect(() => {
    if (!selectedGridDay && dayOptions.length > 0) {
      setSelectedGridDay(dayOptions[0]);
    }
  }, [dayOptions, selectedGridDay]);

  const parseTime = (time) => {
    if (!time || typeof time !== 'string') return null;
    const trimmed = time.trim();
    const match = trimmed.match(/^(\d{1,2})(?::(\d{2}))?\s*(AM|PM)?$/i);
    if (!match) return null;

    let hour = Number(match[1]);
    const minute = Number(match[2] || '0');
    const period = match[3] ? match[3].toUpperCase() : null;

    if (minute < 0 || minute > 59) return null;
    if (period) {
      if (hour < 1 || hour > 12) return null;
      if (hour === 12) {
        hour = period === 'AM' ? 0 : 12;
      } else if (period === 'PM') {
        hour += 12;
      }
    } else {
      if (hour < 0 || hour > 23) return null;
    }

    return hour * 60 + minute;
  };

  const parseTimeRange = (value) => {
    if (!value || typeof value !== 'string') return null;
    const parts = value.split(/\s*-\s*/);
    if (parts.length !== 2) return null;
    const start = parseTime(parts[0].trim());
    const end = parseTime(parts[1].trim());
    if (start === null || end === null) return null;
    return { start, end };
  };

  const formatTime = (minutes) => {
    if (minutes === null || minutes === undefined) return '';
    const hour = Math.floor(minutes / 60);
    const minute = minutes % 60;
    const suffix = hour >= 12 ? 'PM' : 'AM';
    const displayHour = ((hour + 11) % 12) + 1;
    return `${String(displayHour).padStart(2, '0')}:${String(minute).padStart(2, '0')} ${suffix}`;
  };

  const filteredSchedules = useMemo(() => {
    const yearValue = selectedSchoolYear.trim().toLowerCase();
    const roomValue = roomFilter.trim().toLowerCase();
    const departmentValue = departmentFilter.trim().toLowerCase();
    const yearLevelValue = yearLevelFilter.trim().toLowerCase();
    const semesterValue = semesterFilter.trim().toLowerCase();
    const sectionValue = sectionFilter.trim().toLowerCase();

    const compareType = (a, b) => {
      const priority = type => {
        const value = normalizeFilterValue(type).toLowerCase();
        if (value.includes('lec')) return 0;
        if (value.includes('lab')) return 1;
        return 2;
      };
      return priority(a.type) - priority(b.type);
    };

    return schedules
      .filter(row => {
        const rowSchoolYear = normalizeFilterValue(row.schoolYear).toLowerCase();
        const roomName = normalizeFilterValue(row.room).toLowerCase();
        const departmentName = normalizeFilterValue(row.department).toLowerCase();
        const rowYearLevel = normalizeFilterValue(row.yearLevel).toLowerCase();
        const rowSemester = normalizeFilterValue(row.semester).toLowerCase();
        const rowSection = normalizeFilterValue(row.section).toLowerCase();
        const yearMatch = !yearValue || rowSchoolYear === yearValue;
        const roomMatch = !roomValue || roomName.includes(roomValue);
        const departmentMatch = !departmentValue || departmentName === departmentValue;
        const yearLevelMatch = !yearLevelValue || rowYearLevel === yearLevelValue;
        const semesterMatch = !semesterValue || rowSemester === semesterValue;
        const sectionMatch = !sectionValue || rowSection === sectionValue;
        const dayMatch = !dayFilter || parseDayLabels(row.day).includes(dayFilter);
        return yearMatch && roomMatch && departmentMatch && yearLevelMatch && semesterMatch && sectionMatch && dayMatch;
      })
      .sort(compareType);
  }, [schedules, selectedSchoolYear, roomFilter, departmentFilter, yearLevelFilter, semesterFilter, sectionFilter, dayFilter]);

  const sortedSchedules = useMemo(() => {
    const dayOrder = ['Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat', 'Sun'];
    return [...filteredSchedules].sort((a, b) => {
      let aValue = normalizeFilterValue(a[sortField]);
      let bValue = normalizeFilterValue(b[sortField]);
      if (sortField === 'day') {
        aValue = parseDayLabels(a.day).map(day => String(dayOrder.indexOf(day)).padStart(2, '0')).join(',');
        bValue = parseDayLabels(b.day).map(day => String(dayOrder.indexOf(day)).padStart(2, '0')).join(',');
      }
      const result = aValue.localeCompare(bValue, undefined, { numeric: true, sensitivity: 'base' });
      return sortDirection === 'asc' ? result : -result;
    });
  }, [filteredSchedules, sortField, sortDirection]);

  const totalPages = Math.max(1, Math.ceil(sortedSchedules.length / rowsPerPage));
  const displayedPage = Math.min(currentPage, totalPages);
  const paginatedSchedules = sortedSchedules.slice(
    (displayedPage - 1) * rowsPerPage,
    displayedPage * rowsPerPage
  );

  useEffect(() => {
    if (currentPage > totalPages) setCurrentPage(totalPages);
  }, [currentPage, totalPages]);

  const handleSort = (field) => {
    if (sortField === field) {
      setSortDirection(current => current === 'asc' ? 'desc' : 'asc');
      return;
    }
    setSortField(field);
    setSortDirection('asc');
  };

  const sortIndicator = (field) => sortField === field
    ? (sortDirection === 'asc' ? ' ▲' : ' ▼')
    : '';

  const gridTimeSlots = useMemo(() => {
    const slots = new Set();
    const boundaries = [];

    rooms.forEach(room => {
      const start = parseTime(room.roomStartTime);
      const end = parseTime(room.roomEndTime);
      if (start !== null && end !== null && end > start) {
        boundaries.push(start, end);
      }
    });

    filteredSchedules.forEach(row => {
      const segments = getScheduleSegments(row);
      segments.forEach(segment => {
        const segmentDays = parseDayLabels(segment.day);
        if (gridDay && !segmentDays.includes(gridDay)) return;
        const timeRange = parseTimeRange(segment.time);
        if (timeRange) {
          boundaries.push(timeRange.start, timeRange.end);
        }
      });
    });

    if (boundaries.length === 0) return [];

    const minTime = Math.min(...boundaries);
    const maxTime = Math.max(...boundaries);
    const timeStep = 15; // minutes per slot (finer grid)
    for (let time = minTime; time < maxTime; time += timeStep) {
      slots.add(time);
    }
    return Array.from(slots).sort((a, b) => a - b);
  }, [rooms, filteredSchedules, gridDay]);

  // Scale the timetable to the available viewport while keeping short schedules legible.
  const timeStep = 15; // minutes per slot
  const slotHeight = gridTimeSlots.length > 0
    ? Math.max(7, Math.min(18, Math.floor((viewportHeight - 390) / gridTimeSlots.length)))
    : 18;
  const labelStep = 30; // show time labels every 30 minutes

  const getGridSchedules = useMemo(() => {
    const scheduleMap = {};
    const roomKeys = new Map();
    rooms.forEach(room => {
      const key = normalizeRoomKey(room.roomName || '');
      if (key) roomKeys.set(key, room.roomName || '');
    });

    filteredSchedules.forEach(row => {
      const segments = getScheduleSegments(row);
      segments.forEach(segment => {
        const segmentDays = parseDayLabels(segment.day);
        if (gridDay && !segmentDays.includes(gridDay)) return;

        const roomNames = parseRoomLabels(segment.room || row.room);
        if (roomNames.length === 0) roomNames.push('Unassigned');

        const ranges = (segment.time || '')
          .split(/\r?\n|\s*\/\s*/)
          .map(t => parseTimeRange(t.trim()))
          .filter(Boolean);

        if (ranges.length === 0) return;

        roomNames.forEach(roomName => {
          const mappedRoom = findBestRoomLabel(roomName, roomKeys) || roomName;
          if (!scheduleMap[mappedRoom]) scheduleMap[mappedRoom] = [];
          ranges.forEach(range => {
            scheduleMap[mappedRoom].push({
              ...row,
              room: mappedRoom,
              start: range.start,
              end: range.end,
              day: segment.day,
              type: segment.type,
              time: segment.time
            });
          });
        });
      });
    });
    return scheduleMap;
  }, [filteredSchedules, gridDay, rooms]);

  const gridRooms = useMemo(() => {
    return rooms;
  }, [rooms]);

  const visibleGridRooms = useMemo(() => {
    const allRooms = new Map();
    rooms.forEach(room => {
      const name = (room.roomName || '').trim() || 'Unassigned';
      allRooms.set(name, room);
    });
    Object.keys(getGridSchedules).forEach(roomName => {
      if (!allRooms.has(roomName)) {
        allRooms.set(roomName, { id: roomName, roomName, roomType: 'Unassigned' });
      }
    });
    const roomValue = roomFilter.trim().toLowerCase();
    return Array.from(allRooms.values())
      .filter(room => !roomValue || normalizeFilterValue(room.roomName).toLowerCase().includes(roomValue));
  }, [rooms, getGridSchedules, roomFilter]);

  const roomKeyMap = useMemo(() => {
    const map = new Map();
    rooms.forEach(room => {
      const key = normalizeRoomKey(room.roomName || '');
      if (key) map.set(key, room.roomName || '');
    });
    return map;
  }, [rooms]);

  const hasGridEntries = useMemo(() => {
    return Object.values(getGridSchedules).some(entries => entries.length > 0);
  }, [getGridSchedules]);

  const getOrderedRow = (row) => {
    const splitLines = value => typeof value === 'string' ? value.split(/\r?\n/).map(item => item.trim()).filter(Boolean) : [value || ''];
    const splitTypeSegments = (value) => {
      if (typeof value !== 'string') return [value || ''];
      const newlineParts = value.split(/\r?\n/).map(item => item.trim()).filter(Boolean);
      if (newlineParts.length > 1) return newlineParts;
      return value.split(/\s*\/\s*/).map(item => item.trim()).filter(Boolean);
    };

    const daySegments = splitLines(row.day);
    const timeSegments = splitLines(row.time);
    const typeSegments = splitTypeSegments(row.type);
    const roomSegments = splitLines(row.room);

    const segmentCount = Math.max(daySegments.length, timeSegments.length, typeSegments.length, roomSegments.length);
    const segments = Array.from({ length: segmentCount }, (_, index) => ({
      day: daySegments[index] || '',
      time: timeSegments[index] || '',
      type: typeSegments[index] || '',
      room: roomSegments[index] || ''
    }));

    const priority = type => {
      const value = (type || '').toLowerCase();
      if (value.includes('lec')) return 0;
      if (value.includes('lab')) return 1;
      return 2;
    };

    segments.sort((a, b) => priority(a.type) - priority(b.type));

    const joinSegments = (key) => segments.map(segment => segment[key]).filter(Boolean).join('\n');

    return {
      day: joinSegments('day'),
      time: joinSegments('time'),
      type: joinSegments('type'),
      room: joinSegments('room')
    };
  };

  return (
    <div style={{ background: '#fff', borderRadius: 12, padding: 32, boxShadow: '0 2px 12px rgba(0,0,0,0.06)' }}>
      <h2 style={{ marginTop: 0, fontSize: 24, fontWeight: 600, color: '#155c2b', marginBottom: 16 }}>Room Schedule</h2>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 16, marginBottom: 16, flexWrap: 'wrap' }}>
        <p style={{ color: '#667f92', lineHeight: 1.6, fontSize: 14, margin: 0 }}>
          This page shows all saved schedules. Use the filters to narrow results by school year, department, year level, semester, section, day, and room name.
        </p>
        <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
          <button
            type="button"
            onClick={() => setViewMode('table')}
            style={{
              padding: '10px 16px',
              borderRadius: 8,
              border: viewMode === 'table' ? '1px solid #155c2b' : '1px solid #d1d5db',
              background: viewMode === 'table' ? '#155c2b' : '#fff',
              color: viewMode === 'table' ? '#fff' : '#155c2b',
              cursor: 'pointer',
              fontWeight: 600
            }}
          >
            Table View
          </button>
          <button
            type="button"
            onClick={() => setViewMode('grid')}
            style={{
              padding: '10px 16px',
              borderRadius: 8,
              border: viewMode === 'grid' ? '1px solid #155c2b' : '1px solid #d1d5db',
              background: viewMode === 'grid' ? '#155c2b' : '#fff',
              color: viewMode === 'grid' ? '#fff' : '#155c2b',
              cursor: 'pointer',
              fontWeight: 600
            }}
          >
            Grid View
          </button>
        </div>
      </div>

      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4, minmax(0, 1fr))', gap: 16, marginBottom: 24 }}>
        <select
          aria-label="Filter by school year"
          value={selectedSchoolYear}
          onChange={e => {
            setSelectedSchoolYear(e.target.value);
            setCurrentPage(1);
          }}
          style={{ width: '100%', padding: 12, borderRadius: 8, border: '1px solid #d1d5db', fontSize: 14, boxSizing: 'border-box', background: '#fff' }}
        >
          <option value="">All School Years</option>
          {schoolYears.map(year => (
            <option key={year} value={year}>{year}</option>
          ))}
        </select>
        <select
          aria-label="Filter by department"
          value={departmentFilter}
          onChange={e => {
            setDepartmentFilter(e.target.value);
            setCurrentPage(1);
          }}
          style={{ width: '100%', padding: 12, borderRadius: 8, border: '1px solid #d1d5db', fontSize: 14, boxSizing: 'border-box', background: '#fff' }}
        >
          <option value="">All Departments</option>
          {departmentOptions.map(department => (
            <option key={department} value={department}>{department}</option>
          ))}
        </select>
        <select
          aria-label="Filter by year level"
          value={yearLevelFilter}
          onChange={e => {
            setYearLevelFilter(e.target.value);
            setCurrentPage(1);
          }}
          style={{ width: '100%', padding: 12, borderRadius: 8, border: '1px solid #d1d5db', fontSize: 14, boxSizing: 'border-box', background: '#fff' }}
        >
          <option value="">All Year Levels</option>
          {yearLevelOptions.map(yearLevel => (
            <option key={yearLevel} value={yearLevel}>{yearLevel}</option>
          ))}
        </select>
        <select
          aria-label="Filter by semester"
          value={semesterFilter}
          onChange={e => {
            setSemesterFilter(e.target.value);
            setCurrentPage(1);
          }}
          style={{ width: '100%', padding: 12, borderRadius: 8, border: '1px solid #d1d5db', fontSize: 14, boxSizing: 'border-box', background: '#fff' }}
        >
          <option value="">All Semesters</option>
          {semesterOptions.map(semester => (
            <option key={semester} value={semester}>{semester}</option>
          ))}
        </select>
        <select
          aria-label="Filter by section"
          value={sectionFilter}
          onChange={e => {
            setSectionFilter(e.target.value);
            setCurrentPage(1);
          }}
          style={{ width: '100%', padding: 12, borderRadius: 8, border: '1px solid #d1d5db', fontSize: 14, boxSizing: 'border-box', background: '#fff' }}
        >
          <option value="">All Sections</option>
          {sectionOptions.map(section => (
            <option key={section} value={section}>{section}</option>
          ))}
        </select>
        <select
          aria-label="Filter by day"
          value={dayFilter}
          onChange={e => {
            setDayFilter(e.target.value);
            if (e.target.value) {
              setSelectedGridDay(e.target.value);
            } else {
              setSelectedGridDay(dayOptions[0] || '');
            }
            setCurrentPage(1);
          }}
          style={{ width: '100%', padding: 12, borderRadius: 8, border: '1px solid #d1d5db', fontSize: 14, boxSizing: 'border-box', background: '#fff' }}
        >
          <option value="">All Days</option>
          {dayOptions.map(day => (
            <option key={day} value={day}>{day}</option>
          ))}
        </select>
        <input
          type="text"
          aria-label="Filter by room name"
          value={roomFilter}
          onChange={e => {
            setRoomFilter(e.target.value);
            setCurrentPage(1);
          }}
          placeholder="Filter by Room Name"
          style={{ width: '100%', padding: 12, borderRadius: 8, border: '1px solid #d1d5db', fontSize: 14, boxSizing: 'border-box' }}
        />
        <button
          type="button"
          onClick={() => {
            setSelectedSchoolYear('');
            setDepartmentFilter('');
            setYearLevelFilter('');
            setSemesterFilter('');
            setSectionFilter('');
            setDayFilter('');
            setRoomFilter('');
            setCurrentPage(1);
          }}
          style={{ width: '100%', padding: 12, borderRadius: 8, border: '1px solid #155c2b', background: '#fff', color: '#155c2b', fontSize: 14, fontWeight: 600, cursor: 'pointer' }}
        >
          Clear All Filters
        </button>
      </div>

      {statusMessage && (
        <div style={{ marginBottom: 18, padding: 12, borderRadius: 8, background: '#f8d7da', color: '#842029', fontSize: 14 }}>
          {statusMessage}
        </div>
      )}

      

      {viewMode === 'grid' ? (
        <>
          <div style={{ display: 'flex', alignItems: 'center', gap: 12, flexWrap: 'wrap', marginBottom: 12 }}>
            <span style={{ color: '#334155', fontSize: 13, fontWeight: 700 }}>Schedule day</span>
            <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6 }}>
              {dayOptions.length === 0 ? (
                <span style={{ color: '#475569', fontSize: 13 }}>No schedule days available.</span>
              ) : dayOptions.map(day => (
                <button
                  key={day}
                  type="button"
                  aria-pressed={gridDay === day}
                  onClick={() => {
                    setSelectedGridDay(day);
                    setDayFilter(day);
                    setCurrentPage(1);
                  }}
                  style={{
                    padding: '7px 12px',
                    borderRadius: 7,
                    border: gridDay === day ? '1px solid #155c2b' : '1px solid #d1d5db',
                    background: gridDay === day ? '#155c2b' : '#fff',
                    color: gridDay === day ? '#fff' : '#155c2b',
                    cursor: 'pointer',
                    fontSize: 13,
                    fontWeight: 600
                  }}
                >
                  {day}
                </button>
              ))}
            </div>
          </div>

          <p style={{ margin: '0 0 12px', color: '#64748b', fontSize: 13, lineHeight: 1.5 }}>
            Select a day to view its schedule. Click a room name for room details, or click a schedule entry for its details.
          </p>

          <div style={{ marginBottom: 16, width: '100%' }}>
            <div style={{ display: 'grid', gridTemplateColumns: `minmax(58px, 68px) repeat(${Math.max(visibleGridRooms.length, 1)}, minmax(0, 1fr))`, gap: 0, width: '100%' }}>
              <div style={{ padding: '12px 6px', border: '1px solid #e5e7eb', background: '#f5f7fa', fontWeight: 700, position: 'sticky', left: 0, zIndex: 6, display: 'flex', justifyContent: 'center', alignItems: 'center', textAlign: 'center', fontSize: 11 }}>Room / Time</div>
              {visibleGridRooms.map(room => (
                <button
                  key={room.id}
                  type="button"
                  title={`View details for ${room.roomName || 'Unnamed Room'}`}
                  onClick={() => setDetailModal({
                    title: room.roomName || 'Unnamed Room',
                    kind: 'Room details',
                    details: [
                      ['Room Name', room.roomName],
                      ['Type', room.roomType],
                      ['Room Start Time', room.roomStartTime],
                      ['Room End Time', room.roomEndTime],
                      ['Specific Day Entries', room.specificDayEntries],
                      ['Operating Days', room.operatingDays],
                      ['Departments Assigned', room.departmentAssignedNames || room.departments || room.departmentAssignedIds],
                      ['Room Subjects', room.roomSubjectNames || room.roomSubjectName || room.roomSubjectIds]
                    ]
                  })}
                  style={{ width: '100%', minWidth: 0, padding: '7px 3px', border: '1px solid #e5e7eb', background: '#f5f7fa', display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', textAlign: 'center', lineHeight: 1.1, wordBreak: 'break-word', overflowWrap: 'anywhere', cursor: 'pointer', color: '#155c2b', font: 'inherit' }}
                >
                  <span style={{ fontWeight: 700, fontSize: 11, whiteSpace: 'normal', width: '100%' }}>{room.roomName || 'Unnamed Room'}</span>
                </button>
              ))}
            </div>

            <div style={{ display: 'grid', gridTemplateColumns: `minmax(58px, 68px) repeat(${Math.max(visibleGridRooms.length, 1)}, minmax(0, 1fr))`, gap: 0, width: '100%' }}>
              <div style={{ position: 'sticky', left: 0, zIndex: 5, background: '#fff', borderRight: '1px solid #e5e7eb' }}>
                {gridTimeSlots.length === 0 ? (
                  <div style={{ padding: '6px 8px', border: '1px solid #e5e7eb', background: '#fff', fontSize: 11 }}>No time slots available</div>
                ) : (
                  gridTimeSlots.map(slot => (
                    <div key={slot} style={{ height: slotHeight, boxSizing: 'border-box', padding: '2px 4px', borderTop: '1px solid #eef2f7', background: slot % labelStep === 0 ? '#f9fafb' : '#fff', fontSize: 10, display: 'flex', alignItems: 'center' }}>
                      {slot % labelStep === 0 ? formatTime(slot) : ''}
                    </div>
                  ))
                )}
              </div>

              {visibleGridRooms.map(room => {
                const entries = getGridSchedules[room.roomName] || [];
                const totalHeight = gridTimeSlots.length * slotHeight;
                const minTime = gridTimeSlots.length > 0 ? gridTimeSlots[0] : 0;
                return (
                  <div key={room.id} style={{ position: 'relative', minHeight: totalHeight, border: '1px solid #e5e7eb', background: '#fff' }}>
                    {/* render guide rows for alignment */}
                    {gridTimeSlots.map(slot => (
                      <div key={`${room.id}-row-${slot}`} style={{ height: slotHeight, borderTop: '1px solid #f1f5f9', boxSizing: 'border-box' }} />
                    ))}

                    {(() => {
                      const sorted = entries
                        .slice()
                        .sort((a, b) => (a.start || 0) - (b.start || 0) || (a.end || 0) - (b.end || 0));

                      const laneEnds = [];
                      const placements = sorted.map((entry) => {
                        let lane = 0;
                        while (lane < laneEnds.length && (laneEnds[lane] || 0) > (entry.start || 0)) {
                          lane += 1;
                        }
                        laneEnds[lane] = entry.end || entry.start || 0;
                        return { ...entry, lane };
                      });

                      const maxLanes = Math.max(1, ...placements.map(entry => entry.lane + 1));
                      const laneWidth = 100 / maxLanes;

                      return placements.map((entry, index) => {
                        const startOffset = Math.max(0, (entry.start || 0) - minTime);
                        const duration = Math.max(0, (entry.end || 0) - (entry.start || 0));
                        const top = Math.round(startOffset / timeStep) * slotHeight;
                        const rawHeight = Math.round(duration / timeStep) * slotHeight;
                        const height = Math.max(Math.min(rawHeight - 4, totalHeight - top - 4), slotHeight - 4);
                        const left = 4 + entry.lane * laneWidth;
                        const width = Math.max(0, laneWidth - 7);
                        const blockDetails = [
                          entry.courseId || 'TBA',
                          entry.courseName || '',
                          entry.department || '',
                          `${entry.yearLevel || 'N/A'} · ${entry.section || 'N/A'}`,
                          entry.time || 'No time'
                        ].filter(Boolean).join(' | ');

                        return (
                          <button
                            type="button"
                            key={`${room.id}-${entry.id || index}-${entry.start}-${entry.end}`}
                            title={`View schedule details: ${blockDetails}`}
                            aria-label={blockDetails}
                            onClick={() => setDetailModal({
                              title: entry.room || 'Schedule details',
                              kind: 'Schedule details',
                              details: [
                                ['Room', entry.room],
                                ['Time', entry.time],
                                ['Day', entry.day],
                                ['Semester', entry.semester],
                                ['Department', entry.department],
                                ['Year Level', entry.yearLevel],
                                ['Section', entry.section],
                                ['Course Name', entry.courseName],
                                ['Professor', entry.professor || entry.prof || entry.professorName]
                              ]
                            })}
                            style={{
                              position: 'absolute',
                              top,
                              left: `${left}%`,
                              width: `${width}%`,
                              height,
                              background: '#dcfce7',
                              border: '1px solid #34d399',
                              borderRadius: 6,
                              padding: '3px',
                              fontSize: 10,
                              lineHeight: 1.05,
                              overflow: 'hidden',
                              color: '#0f172a',
                              boxSizing: 'border-box',
                              cursor: 'pointer',
                              textAlign: 'left',
                              fontFamily: 'inherit'
                            }}
                          >
                            <span style={{ display: 'block', fontWeight: 700 }}>{entry.courseId || 'TBA'}</span>
                            {height >= 32 && <span style={{ display: 'block', fontSize: 9, marginTop: 1 }}>{entry.section || ''}</span>}
                            {height >= 48 && <span style={{ display: 'block', fontSize: 9, marginTop: 1 }}>{entry.time || ''}</span>}
                          </button>
                        );
                      });
                    })()}
                    {/* removed empty-room placeholder per request */}
                  </div>
                );
              })}
            </div>
          </div>

          {!hasGridEntries && filteredSchedules.length > 0 && (
            <div style={{ marginTop: 20, padding: 16, borderRadius: 12, background: '#f8fafc', border: '1px solid #c7d2fe', color: '#1e3a8a' }}>
              <div style={{ fontWeight: 700, marginBottom: 8 }}>No chart blocks available.</div>
              <div style={{ marginBottom: 8 }}>The filtered schedules exist, but no valid room/time blocks could be rendered in the grid.</div>
              <div style={{ fontSize: 13, lineHeight: 1.5 }}>
                {filteredSchedules.map((row, index) => (
                  <div key={row.id || index} style={{ marginBottom: 4 }}>
                    • {row.courseId || 'N/A'} / {row.courseName || 'N/A'} / {row.day || 'No day'} / {row.time || 'No time'} / {row.room || 'No room'}
                  </div>
                ))}
              </div>
            </div>
          )}
        </>
      ) : (
        <div style={{ overflowX: 'auto' }}>
          <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 14, minWidth: 1100 }}>
            <thead>
              <tr style={{ background: '#f5f7fa', textAlign: 'left' }}>
                {[
                  ['department', 'Department'],
                  ['yearLevel', 'Year Level'],
                  ['semester', 'Semester'],
                  ['section', 'Section'],
                  ['courseId', 'Course ID'],
                  ['day', 'Day'],
                  ['time', 'Time'],
                  ['type', 'Type'],
                  ['room', 'Room']
                ].map(([field, label]) => (
                  <th key={field} style={{ padding: 12, border: '1px solid #e5e7eb' }}>
                    <button
                      type="button"
                      onClick={() => handleSort(field)}
                      aria-label={`Sort by ${label} ${sortField === field && sortDirection === 'asc' ? 'descending' : 'ascending'}`}
                      style={{ padding: 0, border: 0, background: 'transparent', color: 'inherit', font: 'inherit', fontWeight: 700, textAlign: 'left', cursor: 'pointer' }}
                    >
                      {label}{sortIndicator(field)}
                    </button>
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {sortedSchedules.length === 0 ? (
                <tr>
                  <td colSpan={9} style={{ padding: 24, textAlign: 'center', color: '#667f92' }}>
                    {schedules.length === 0 ? 'No schedules have been saved yet.' : 'No schedules match the selected filters.'}
                  </td>
                </tr>
              ) : (
                paginatedSchedules.map((row) => (
                  <tr key={row.id} style={{ background: '#fff' }}>
                    <td style={{ padding: 12, border: '1px solid #e5e7eb' }}>{row.department || 'N/A'}</td>
                    <td style={{ padding: 12, border: '1px solid #e5e7eb' }}>{row.yearLevel || 'N/A'}</td>
                    <td style={{ padding: 12, border: '1px solid #e5e7eb' }}>{row.semester || 'N/A'}</td>
                    <td style={{ padding: 12, border: '1px solid #e5e7eb' }}>{row.section || 'N/A'}</td>
                    <td
                      title={row.courseName || ''}
                      style={{ padding: 12, border: '1px solid #e5e7eb', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis', maxWidth: 140 }}
                    >
                      {row.courseId || 'N/A'}
                    </td>
                    {(() => {
                      const ordered = getOrderedRow(row);
                      return (
                        <>
                          <td style={{ padding: 12, border: '1px solid #e5e7eb', whiteSpace: 'pre-line', wordBreak: 'break-word' }}>{ordered.day || 'N/A'}</td>
                          <td style={{ padding: 12, border: '1px solid #e5e7eb', whiteSpace: 'pre-line', wordBreak: 'break-word' }}>{ordered.time || 'N/A'}</td>
                          <td style={{ padding: 12, border: '1px solid #e5e7eb', whiteSpace: 'pre-line', wordBreak: 'break-word' }}>{ordered.type || 'N/A'}</td>
                          <td style={{ padding: 12, border: '1px solid #e5e7eb', whiteSpace: 'pre-line', wordBreak: 'break-word' }}>{ordered.room || 'TBA'}</td>
                        </>
                      );
                    })()}
                  </tr>
                ))
              )}
            </tbody>
          </table>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 12, flexWrap: 'wrap', paddingTop: 16 }}>
            <span style={{ color: '#475569', fontSize: 14 }}>
              {sortedSchedules.length === 0
                ? 'Showing 0 of 0 schedules'
                : `Showing ${(displayedPage - 1) * rowsPerPage + 1}-${Math.min(displayedPage * rowsPerPage, sortedSchedules.length)} of ${sortedSchedules.length} schedules`}
            </span>
            <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
              <button
                type="button"
                onClick={() => setCurrentPage(page => Math.max(1, page - 1))}
                disabled={displayedPage === 1}
                style={{ padding: '8px 12px', borderRadius: 6, border: '1px solid #d1d5db', background: displayedPage === 1 ? '#f3f4f6' : '#fff', color: '#155c2b', cursor: displayedPage === 1 ? 'not-allowed' : 'pointer' }}
              >
                Previous
              </button>
              <span style={{ color: '#475569', fontSize: 14 }}>Page {displayedPage} of {totalPages}</span>
              <button
                type="button"
                onClick={() => setCurrentPage(page => Math.min(totalPages, page + 1))}
                disabled={displayedPage === totalPages}
                style={{ padding: '8px 12px', borderRadius: 6, border: '1px solid #d1d5db', background: displayedPage === totalPages ? '#f3f4f6' : '#fff', color: '#155c2b', cursor: displayedPage === totalPages ? 'not-allowed' : 'pointer' }}
              >
                Next
              </button>
            </div>
          </div>
        </div>
      )}
      {detailModal && (
        <div
          role="presentation"
          onMouseDown={event => {
            if (event.target === event.currentTarget) setDetailModal(null);
          }}
          style={{ position: 'fixed', inset: 0, zIndex: 3000, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 20, background: 'rgba(15, 23, 42, 0.55)' }}
        >
          <section
            role="dialog"
            aria-modal="true"
            aria-labelledby="room-schedule-detail-title"
            style={{ width: '100%', maxWidth: 620, maxHeight: '85vh', overflowY: 'auto', padding: 24, borderRadius: 12, background: '#fff', boxShadow: '0 18px 48px rgba(0,0,0,0.24)' }}
          >
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: 16, marginBottom: 18 }}>
              <div>
                <div style={{ marginBottom: 4, color: '#64748b', fontSize: 12, fontWeight: 700, textTransform: 'uppercase', letterSpacing: 0.5 }}>{detailModal.kind}</div>
                <h2 id="room-schedule-detail-title" style={{ margin: 0, color: '#155c2b', fontSize: 22 }}>{detailModal.title}</h2>
              </div>
              <button type="button" autoFocus onClick={() => setDetailModal(null)} aria-label="Close details" style={{ padding: '7px 12px', border: '1px solid #cbd5e1', borderRadius: 7, background: '#fff', color: '#334155', cursor: 'pointer', fontWeight: 600 }}>
                Close
              </button>
            </div>
            <dl style={{ display: 'grid', gridTemplateColumns: 'minmax(120px, 0.8fr) minmax(0, 1.6fr)', gap: '0 16px', margin: 0 }}>
              {detailModal.details.map(([key, value]) => (
                <React.Fragment key={key}>
                  <dt style={{ padding: '10px 0', borderTop: '1px solid #e2e8f0', color: '#475569', fontSize: 13, fontWeight: 700 }}>{key}</dt>
                  <dd style={{ minWidth: 0, margin: 0, padding: '10px 0', borderTop: '1px solid #e2e8f0', color: '#1e293b', fontSize: 14, overflowWrap: 'anywhere' }}>
                    <DetailValue value={value} />
                  </dd>
                </React.Fragment>
              ))}
            </dl>
          </section>
        </div>
      )}
    </div>
  );
}
